import type {
  UseFunnelChannelSessionsParams,
  UseFunnelChannelSessionsResponse,
} from '../../../types/api/funnels/funnel-channel-sessions.types';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemoizedArrayData } from 'src/hooks/api/api';
import { awaitCancelableRequest } from 'src/modules/shared/utils/api/cancelable-request.util';
import {
  CrmFunnelsChannelSessionsService,
  type FunnelChannelSession,
  type FunnelChannelSessionListResponse,
} from 'src/api/generated';

export const useFunnelChannelSessions = (
  params: UseFunnelChannelSessionsParams,
  enabled = true
): UseFunnelChannelSessionsResponse => {
  const fetchChannelSessions = async ({
    pageParam,
    signal,
  }: {
    pageParam: string | undefined;
    signal: AbortSignal;
  }): Promise<FunnelChannelSessionListResponse> => {
    const { filterBy, searchBy, sortBy, page, pageSize } = params;

    return await awaitCancelableRequest(
      CrmFunnelsChannelSessionsService.getFunnelChannelSessions(
        filterBy,
        searchBy,
        sortBy,
        page,
        pageSize,
        pageParam
      ),
      signal
    );
  };

  const getNextPageParam = (lastPage: FunnelChannelSessionListResponse) =>
    lastPage.nextPageToken;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['crm/funnel-channel-sessions', params],
    queryFn: fetchChannelSessions,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
  });

  const channelSessions = useMemoizedArrayData<FunnelChannelSession>(
    data?.pages.flatMap((page) => page.items) ?? []
  );

  return {
    loading,
    error,
    channelSessions,
    fetchNextPage: () => {
      void fetchNextPage();
    },
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};
